import React, { Fragment, useEffect, useState } from "react";
import { CommonSelect, Input, TextArea } from "../../components/Form";
import ModalBox from "../../components/modal/ModalBox";
import { toastMessage } from "../../utils/toastMessage";
import {
  sendNotificationApi,
  sendTargetedNotificationApi,
} from "../../services/notificationManagement";
import { getPowerCustomers } from "../../services/powerCustomer";
import { getUserListApi } from "../../services/userManagement";
import { fatchCategoryApi } from "../../services/categoryManagement";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { buildNotificationTranslationsPayload } from "../../utils/appLanguages";
import NotificationLanguageFields from "./NotificationLanguageFields";

const cohortTypes = {
  main_power: { customerType: "power", label: "Main Power" },
  potential: { customerType: "potential", label: "Potential" },
};

const AddNotification = ({ show, close, listing }) => {
  const [inputField, setInputField] = useState({});
  const [error, setError] = useState({});
  const [buttonref, buttonLoader] = useButtonLoader("Send");
  const userTypeOptions = [
    { label: "All", value: "all" },
    { label: "User", value: "user" },
    { label: "Consultant", value: "cons" },
  ];
  const consultantUsersTypeOptions = [
    { label: "Sign up but not consult", value: "signup_not_consult" },
    { label: "First offer consultation done but not 2nd", value: "first_consult_not_second" },
    { label: "Not Repeat - count after 3rd consultation", value: "not_repeat_after_third" },
    { label: "Main Power", value: "main_power" },
    { label: "Potential", value: "potential" },
    { label: "Power Customers (custom date range)", value: "power_customers" },
    { label: "All Users", value: "all_users" },
  ];
  const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
  ];
  const [userTypeValue, setUserTypeValue] = useState("");
  const [dropdownType, setDropdownType] = useState("");
  const [gender, setGender] = useState("");
  const [powerStartDate, setPowerStartDate] = useState("");
  const [powerEndDate, setPowerEndDate] = useState("");
  const [powerAudience, setPowerAudience] = useState({ loading: false, count: null, userIds: [] });
  const [cohortAudience, setCohortAudience] = useState({ loading: false, count: null, userIds: [] });
  const [problemCategories, setProblemCategories] = useState([]);
  const [problemOptions, setProblemOptions] = useState([]);
  const [problemOptionsLoading, setProblemOptionsLoading] = useState(false);

  // Reset Consultant & Users dropdowns when user type changes
  useEffect(() => {
    if (userTypeValue !== "user") {
      setDropdownType("");
      setGender("");
      setProblemCategories([]);
    }
  }, [userTypeValue]);

  useEffect(() => {
    let active = true;
    if (!show) return () => { active = false; };

    setProblemOptionsLoading(true);
    fatchCategoryApi()
      .then(({ data }) => {
        if (!active) return;
        const options = (data?.result?.categoryList || [])
          .filter(({ title }) => Boolean(title))
          .map(({ _id, title }) => ({ value: title, label: title, id: _id }));
        setProblemOptions(options);
      })
      .catch(() => {
        if (active) {
          setProblemOptions([]);
          toastMessage("Unable to load problem categories", "error", "problemCategories");
        }
      })
      .finally(() => active && setProblemOptionsLoading(false));

    return () => { active = false; };
  }, [show]);

  useEffect(() => {
    let active = true;
    if (dropdownType !== "power_customers" || !powerStartDate || !powerEndDate || powerStartDate > powerEndDate) {
      setPowerAudience({ loading: false, count: null, userIds: [] });
      return () => { active = false; };
    }
    setPowerAudience((current) => ({ ...current, loading: true }));
    getPowerCustomers({ startDate: powerStartDate, endDate: powerEndDate })
      .then((result) => {
        if (!active) return;
        const userIds = (result.candidates || [])
          .filter((candidate) => candidate.isPowerCustomer)
          .map((candidate) => candidate.userId);
        setPowerAudience({ loading: false, count: userIds.length, userIds });
      })
      .catch(() => active && setPowerAudience({ loading: false, count: null, userIds: [] }));
    return () => { active = false; };
  }, [dropdownType, powerStartDate, powerEndDate]);

  const selectedCohort = cohortTypes[dropdownType] || null;

  useEffect(() => {
    let active = true;
    if (!selectedCohort || !gender) {
      setCohortAudience({ loading: false, count: null, userIds: [] });
      return () => { active = false; };
    }
    setCohortAudience({ loading: true, count: null, userIds: [] });
    getUserListApi({
      fetchAllIds: true,
      page: "1",
      limit: "10",
      filter: {
        customerType: selectedCohort.customerType,
        gender,
      },
    })
      .then(({ data }) => {
        if (!active) return;
        if (data?.code !== 200) {
          setCohortAudience({ loading: false, count: null, userIds: [] });
          return;
        }
        const userIds = data?.result?.list || [];
        setCohortAudience({ loading: false, count: userIds.length, userIds });
      })
      .catch(() => active && setCohortAudience({ loading: false, count: null, userIds: [] }));
    return () => { active = false; };
  }, [dropdownType, gender]);

  const hasTargetedAudience =
    dropdownType === "power_customers" || Boolean(selectedCohort) || problemCategories.length > 0;

  // Onchange Handler
  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setInputField({ ...inputField, [name]: value });
  };

  // Input field Validation
  const validation = () => {
    let status = true;
    const errors = {};
    // if (!inputField?.title) {
    //   errors["title"] = "Title is mandatory";
    //   status = false;
    // }
    if (!inputField?.message) {
      errors["message"] = "Message is mandatory";
      status = false;
    }
    // if (!inputField?.activeUserType) {
    //   errors["activeUserType"] = "Choose  user type";
    //   status = false;
    // }
    if (!userTypeValue) {
      errors["userType"] = "Select user type";
      status = false;
    }
    if (dropdownType === "power_customers") {
      if (!powerStartDate) {
        errors["powerStartDate"] = "Start date is required";
        status = false;
      }
      if (!powerEndDate) {
        errors["powerEndDate"] = "End date is required";
        status = false;
      }
      if (powerStartDate && powerEndDate && powerStartDate > powerEndDate) {
        errors["powerEndDate"] = "End date must be on or after start date";
        status = false;
      }
      if (powerAudience.loading) {
        errors["powerAudience"] = "Please wait while recipients are being calculated";
        status = false;
      }
      if (!powerAudience.loading && powerAudience.count === 0) {
        errors["powerAudience"] = "No Power Customers found for this date range";
        status = false;
      }
      if (!powerAudience.loading && powerStartDate && powerEndDate && powerAudience.count === null) {
        errors["powerAudience"] = "Unable to calculate recipients. Please retry the date range.";
        status = false;
      }
    }
    if (selectedCohort) {
      if (!gender) {
        errors["gender"] = `Select Male or Female for ${selectedCohort.label}`;
        status = false;
      }
      if (cohortAudience.loading) {
        errors["cohortAudience"] = "Please wait while recipients are being calculated";
        status = false;
      }
      if (!cohortAudience.loading && gender && cohortAudience.count === 0) {
        errors["cohortAudience"] = `No ${selectedCohort.label} ${gender} users found`;
        status = false;
      }
      if (!cohortAudience.loading && gender && cohortAudience.count === null) {
        errors["cohortAudience"] = "Unable to calculate recipients. Please try again.";
        status = false;
      }
    }
    setError(errors);
    return status;
  };

  // Add Notification Handler
  const addNotificationHandler = async () => {
    const status = validation();
    if (!status) return;

    const translations = buildNotificationTranslationsPayload(inputField);
    const reqData = { ...inputField, ...translations, userType: userTypeValue,activeUserType: "all" };
    if (userTypeValue === "user") {
      if (dropdownType) reqData.segmentType = dropdownType;
      if (gender) reqData.gender = gender;
    }

    try {
      buttonLoader(true);
      const { data } = hasTargetedAudience
        ? await sendTargetedNotificationApi({
            title: translations.title || "Vedic Meet",
            message: translations.message,
            titleHi: translations.titleHi,
            messageHi: translations.messageHi,
            titleKn: translations.titleKn,
            messageKn: translations.messageKn,
            ...(problemCategories.length > 0 ? { userProblems: problemCategories } : {}),
            ...(dropdownType === "power_customers"
              ? {
                  userIds: powerAudience.userIds,
                  segmentType: "power_customers",
                  startDate: powerStartDate,
                  endDate: powerEndDate,
                }
              : {}),
            ...(selectedCohort
              ? {
                  userIds: cohortAudience.userIds,
                  segmentType: dropdownType,
                  gender,
                }
              : {}),
          })
        : await sendNotificationApi(reqData);

      if (data.code === 200) {
        if (inputField.scheduledAt) {
          toastMessage("Notification will be sent at the scheduled time", "success");
        } else {
          toastMessage(data.message || "Notification sending started successfully", "success");
        }
        listing();
        close();
      } else {
        toastMessage(data.message, "error", "addNotification");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage(
        error?.response?.data?.message ||
          "Unable to send notification. Please check the server and try again.",
        "error",
        "addNotification"
      );
    } finally {
      buttonLoader(false);
    }
  };



  // console.log(error, "Error message");

  return (
    <ModalBox
      status={show}
      onClose={close}
      title={"Add Notification"}
      size={"lg"}
      closeIcon
      custom
      customizedFooter={
        <div className="row">
          {/* <div className="col-12">
            <div className="form-group mb-2">
              <div>
                <label className="my-1">Select user type</label>
                <span className="text-danger"> *</span>
              </div>
              <div className="checkbox_Wrap">
                <label>
                  <span>All</span>{" "}
                  <input
                    type="radio"
                    name="activeUserType"
                    value="all"
                    checked={inputField?.activeUserType === "all"}
                    onChange={onChangeHandler}
                  />
                </label>
                <label>
                  <span>Active</span>
                  <input
                    type="radio"
                    name="activeUserType"
                    value="active"
                    checked={inputField?.activeUserType === "active"}
                    onChange={onChangeHandler}
                  />
                </label>
                <label>
                  <span>In-active</span>
                  <input
                    type="radio"
                    name="activeUserType"
                    value="inactive"
                    checked={inputField?.activeUserType === "inactive"}
                    onChange={onChangeHandler}
                  />
                </label>
              


              </div>
              {error.activeUserType && (
                <div className="invalid-feedback d-block f-2">
                  {error.activeUserType}
                </div>
              )}
            </div>
          </div> */}
         
          {/* <div className="col-12">
            <div className="form-group select_sm_width">
              <CommonSelect
                className="add-consultant"
                label={"Gender"}
                options={genderOptions}
                onChange={setGender}
              />
            </div>
          </div> */}
          {/* <div className="col-12">
            <div className="form-group mb-2">
              <Input
                type="text"
                name="age"
                value={inputField?.age || ""}
                // required
                onChange={onChangeHandler}
                placeholder="Age"
                className={`form-control `}
                label={"Age"}
              // error={!addConsultantValue?.name && error.name}
              maxLength={3}
              />
            </div>
          </div> */}
          <div className="col-12">
            <div className="form-group select_sm_width">
              <CommonSelect
                className="add-consultant"
                label={"User Type"}
                options={userTypeOptions}
                onChange={setUserTypeValue}
                required
                error={!userTypeValue && error.userType}
              />
            </div>
          </div>

          {userTypeValue === "user" && (
            <>
              <div className="col-12">
                <div className="form-group select_sm_width">
                  <CommonSelect
                    className="add-consultant"
                    label={"Type"}
                    options={consultantUsersTypeOptions}
                    value={consultantUsersTypeOptions.find((o) => o.value === dropdownType) || null}

                    onChange={setDropdownType}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group select_sm_width">
                  <CommonSelect
                    className="add-consultant"
                    label={"Problem Category"}
                    options={problemOptions}
                    value={problemOptions.filter((option) => problemCategories.includes(option.value))}
                    onChange={setProblemCategories}
                    loading={problemOptionsLoading}
                    isMulti
                    isSearchable
                  />
                </div>
              </div>
              {dropdownType === "power_customers" && (
                <>
                  <div className="col-6">
                    <div className="form-group mb-2">
                      <label>Power Customer start date <span className="text-danger">*</span></label>
                      <input
                        className="form-control"
                        type="date"
                        value={powerStartDate}
                        onChange={(event) => setPowerStartDate(event.target.value)}
                      />
                      {error.powerStartDate && <div className="invalid-feedback d-block">{error.powerStartDate}</div>}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-2">
                      <label>Power Customer end date <span className="text-danger">*</span></label>
                      <input
                        className="form-control"
                        type="date"
                        min={powerStartDate}
                        value={powerEndDate}
                        onChange={(event) => setPowerEndDate(event.target.value)}
                      />
                      {error.powerEndDate && <div className="invalid-feedback d-block">{error.powerEndDate}</div>}
                    </div>
                  </div>
                  <div className="col-12 mb-2">
                    <div className={`alert ${powerAudience.count ? "alert-info" : "alert-secondary"} py-2`}>
                      {powerAudience.loading
                        ? "Calculating Power Customer audience..."
                        : powerAudience.count === null
                          ? "Select a valid date range to preview recipients."
                          : problemCategories.length > 0
                            ? `${powerAudience.count} Power Customers found before applying the problem filter.`
                            : `${powerAudience.count} Power Customer${powerAudience.count === 1 ? "" : "s"} will receive this notification.`}
                    </div>
                    {error.powerAudience && <div className="invalid-feedback d-block">{error.powerAudience}</div>}
                  </div>
                </>
              )}
              <div className="col-12">
                <div className="form-group select_sm_width">
                  <CommonSelect
                    className="add-consultant"
                    label={selectedCohort ? `${selectedCohort.label} gender` : "Gender"}
                    options={genderOptions}
                    value={genderOptions.find((o) => o.value === gender) || null}
                    onChange={setGender}
                    required={Boolean(selectedCohort)}
                    error={selectedCohort && !gender && error.gender}
                  />
                </div>
              </div>
              {selectedCohort && (
                <div className="col-12 mb-2">
                  <div className={`alert ${cohortAudience.count ? "alert-info" : "alert-secondary"} py-2`}>
                    {!gender
                      ? `Select Male or Female to target ${selectedCohort.label} users.`
                      : cohortAudience.loading
                        ? `Calculating ${selectedCohort.label} ${gender} audience...`
                        : cohortAudience.count === null
                          ? "Unable to preview recipients. Change the selection to retry."
                          : problemCategories.length > 0
                            ? `${cohortAudience.count} ${selectedCohort.label} ${gender} user${cohortAudience.count === 1 ? "" : "s"} found before applying the problem filter.`
                            : `${cohortAudience.count} ${selectedCohort.label} ${gender} user${cohortAudience.count === 1 ? "" : "s"} will receive this notification.`}
                  </div>
                  {error.cohortAudience && <div className="invalid-feedback d-block">{error.cohortAudience}</div>}
                </div>
              )}
            </>
          )}
           <NotificationLanguageFields values={inputField} onChange={onChangeHandler} error={error} />

          <div className="text-center">
            <button
              className="btn_size text-center"
              onClick={addNotificationHandler}
              ref={buttonref}
            >
              Send
            </button>
          </div>
        </div>
      }
    />
  );
};

export default AddNotification;
